import type { ListDefinition } from '@fc/core'
import { Badge, Button, Card, CardContent, Input, Label, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Play, RefreshCw, Save, Trash2, Users } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { deleteList, getList, materializeList, previewList, updateList } from '../../lib/lists-api'
import { RuleBuilder } from './rule-builder'

const EMPTY_DEFINITION: ListDefinition = { combinator: 'and', rules: [] }

export function ListDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['lists', id],
    queryFn: () => getList(id),
    enabled: Boolean(id),
  })

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [definition, setDefinition] = useState<ListDefinition>(EMPTY_DEFINITION)

  // Hydrate local edit state when the list loads.
  useEffect(() => {
    if (data) {
      setName(data.name)
      setDescription(data.description ?? '')
      setDefinition(data.definition ?? EMPTY_DEFINITION)
    }
  }, [data])

  const dirty = useMemo(() => {
    if (!data) return false
    return (
      name !== data.name ||
      (description ?? '') !== (data.description ?? '') ||
      JSON.stringify(definition) !== JSON.stringify(data.definition ?? EMPTY_DEFINITION)
    )
  }, [data, name, description, definition])

  const save = useMutation({
    mutationFn: () => updateList(id, { name, description: description || null, definition }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['lists', id], updated)
      queryClient.invalidateQueries({ queryKey: ['lists'] })
    },
  })

  const preview = useMutation({
    mutationFn: () => previewList(id, definition),
  })

  const materialize = useMutation({
    mutationFn: () => materializeList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists', id] })
      queryClient.invalidateQueries({ queryKey: ['lists'] })
      preview.reset()
    },
  })

  const remove = useMutation({
    mutationFn: () => deleteList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] })
      navigate('/app/lists')
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-muted">
        <Spinner /> Loading segment…
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/app/lists')}>
          <ArrowLeft />
          Back to lists
        </Button>
        <Card>
          <CardContent className="py-16 text-center">
            <p className="text-sm text-danger">Couldn’t load this segment.</p>
            <p className="mt-1 text-xs text-dim">
              {error instanceof Error ? error.message : 'It may have been deleted.'}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const previewResult = preview.data

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/app/lists')} className="mb-3">
          <ArrowLeft />
          Back to lists
        </Button>
        <PageHeader
          title={data.name}
          description="Define who belongs in this segment. Dynamic segments re-evaluate on demand."
          actions={
            <>
              <Button
                variant="ghost"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
                aria-label="Delete segment"
              >
                <Trash2 />
                Delete
              </Button>
              <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
                {save.isPending ? <Spinner /> : <Save />}
                Save
              </Button>
            </>
          }
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
        {/* Editor column */}
        <div className="space-y-6">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="space-y-1.5">
                <Label htmlFor="list-name">Name</Label>
                <Input
                  id="list-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={160}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="list-desc">Description</Label>
                <Input
                  id="list-desc"
                  value={description}
                  placeholder="What is this segment for?"
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={2000}
                />
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-dim">
                Conditions
              </h2>
              <Badge
                variant={data.kind === 'dynamic' ? 'success' : 'neutral'}
                className="capitalize"
              >
                {data.kind}
              </Badge>
            </div>
            <RuleBuilder definition={definition} onChange={setDefinition} />
          </div>
        </div>

        {/* Preview / actions column */}
        <div className="space-y-4">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Live preview</span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => preview.mutate()}
                  disabled={preview.isPending}
                >
                  {preview.isPending ? <Spinner /> : <Play />}
                  Preview
                </Button>
              </div>

              {preview.isError ? (
                <p className="text-sm text-danger">
                  {preview.error instanceof Error
                    ? preview.error.message
                    : 'Could not evaluate the segment.'}
                </p>
              ) : previewResult ? (
                <>
                  <div className="flex items-baseline gap-2">
                    <span className="text-gradient font-display text-3xl font-bold">
                      {previewResult.count.toLocaleString()}
                    </span>
                    <span className="text-sm text-muted">
                      {previewResult.count === 1 ? 'person matches' : 'people match'}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs uppercase tracking-wider text-dim">Sample</div>
                    {previewResult.sample.length === 0 ? (
                      <p className="text-sm text-dim">No one matches yet.</p>
                    ) : (
                      <ul className="divide-y divide-border-soft">
                        {previewResult.sample.map((p) => (
                          <li key={p.id} className="flex items-center justify-between py-1.5">
                            <span className="text-sm">
                              {p.firstName} {p.lastName}
                            </span>
                            <span className="text-xs text-dim">{p.email ?? '—'}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm text-dim">
                  Run a preview to see how many people match these conditions.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 pt-6">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Users className="size-4 text-secondary" />
                Materialized members
              </div>
              <p className="text-sm text-muted">
                {data.cachedCount === null
                  ? 'Not yet materialized.'
                  : `${data.cachedCount.toLocaleString()} members as of ${
                      data.lastEvaluatedAt
                        ? new Date(data.lastEvaluatedAt).toLocaleString()
                        : 'unknown'
                    }.`}
              </p>
              <Button
                className="w-full"
                variant="secondary"
                onClick={() => materialize.mutate()}
                disabled={materialize.isPending || dirty || data.kind !== 'dynamic'}
              >
                {materialize.isPending ? <Spinner /> : <RefreshCw />}
                Materialize now
              </Button>
              {dirty && <p className="text-xs text-dim">Save your changes before materializing.</p>}
              {materialize.isError && (
                <p className="text-xs text-danger">
                  {materialize.error instanceof Error
                    ? materialize.error.message
                    : 'Materialize failed.'}
                </p>
              )}
              {materialize.data && (
                <p className="text-xs text-success">
                  Updated: {materialize.data.added} added, {materialize.data.removed} removed.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
